import { GrowthBook } from '@growthbook/growthbook-react';
import { useCallback, useEffect } from 'react';
import { useStickyState } from './useStickyState';
import { tenantId, growthBookUrl } from 'tools';

const growthbook = new GrowthBook({
  // enableDevMode: true allows you to use the Chrome DevTools Extension to test/debug.
  enableDevMode: false,
  // Callback when a user is put into an A/B experiment
  trackingCallback: () => {},
});

export const useGrowthBook = (): GrowthBook => {
  const IS_GROWTHBOOK_CALL = 'IS_GROWTHBOOK_CALL';
  const TenantID = tenantId;
  const [growthBookStickyData, setGrowthBookStickyData] = useStickyState(
    '',
    IS_GROWTHBOOK_CALL,
    true
  );

  /* This code is making a GET request to the URL specified in the `growthBookUrl` variable, and then
parsing the response as JSON. If the `growthBookStickyData` state variable is empty (i.e. it has no
keys), it sets the state to the parsed JSON data. This allows the GrowthBook instance to use the
features defined in the JSON data to run experiments and variations. */
  fetch(growthBookUrl)
    .then((res) => res.json())
    .then((json) => {
      if (Object.keys(growthBookStickyData).length === 0) {
        setGrowthBookStickyData(json);
      }
    })
    .catch(() => {
      setGrowthBookStickyData('');
    });

  const initGrowthbook = useCallback(async (features) => {
    await growthbook.init({
      payload: {
        features: features,
      },
    });
  }, []);

  /* This is a React `useEffect` hook that sets the features of the `growthbook` instance. It takes
`growthBookStickyData` as a dependency, and will re-run whenever this value changes. If
`growthBookStickyData` is truthy, it parses the data and sets the features of the `growthbook`
instance to the `features` property of the parsed data. This allows GrowthBook to use the features
defined in the JSON data to run experiments and variations. */
  useEffect(() => {
    if (growthBookStickyData) {
      const parsedData = JSON.parse(JSON.stringify(growthBookStickyData));
      initGrowthbook(parsedData.features).catch();
    }
  }, [growthBookStickyData, initGrowthbook]);

  /* This is a React `useEffect` hook that sets the `tenantID` attribute of the `growthbook` instance. It
takes `TenantID` as a dependency, and will re-run whenever this value changes. This allows
GrowthBook to track experiments and variations by the `tenantID` attribute. */
  useEffect(() => {
    growthbook.setAttributes({
      tenantID: TenantID,
    });
  }, [TenantID]);

  return growthbook;
};
